import React from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { SupportAnalytics, getTicketStatusColor } from './shared'

export const SupportAnalyticsPage: React.FC = () => {
  const { t } = useTranslation()

  const { data: analytics, isLoading } = useQuery({
    queryKey: ['support-module-analytics'],
    queryFn: async () => {
      const response = await api.get('/support/analytics')
      return response.data.data as SupportAnalytics
    },
  })

  if (isLoading) {
    return <div className="p-8 text-center text-gray-400">{t('supportModule.loading')}</div>
  }

  if (!analytics) {
    return <div className="p-8 text-center text-gray-400">{t('supportModule.noData')}</div>
  }

  const kpiCards = [
    { label: t('supportModule.ticketsToday'), value: analytics.tickets.today },
    { label: t('supportModule.ticketsWeek'), value: analytics.tickets.week },
    { label: t('supportModule.ticketsMonth'), value: analytics.tickets.month },
    { label: t('supportModule.ticketsTotal'), value: analytics.tickets.total },
  ]

  const maxStatusCount = Math.max(1, ...Object.values(analytics.status))
  const resolutionLabels: Record<string, string> = {
    bot: t('supportModule.resolutionBot'),
    agent: t('supportModule.resolutionAgent'),
    unknown: t('supportModule.resolutionUnknown'),
  }
  const maxResolutionCount = Math.max(1, ...Object.values(analytics.resolution))

  return (
    <div className="space-y-6">
      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map((card) => (
          <div key={card.label} className="bg-[#12121a] border border-white/10 rounded-xl p-6">
            <p className="text-sm text-gray-400 mb-1">{card.label}</p>
            <p className="text-3xl font-bold">{card.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By status */}
        <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
          <h3 className="font-medium mb-4">{t('supportModule.byStatus')}</h3>
          {Object.keys(analytics.status).length === 0 ? (
            <p className="text-sm text-gray-500">{t('supportModule.noData')}</p>
          ) : (
            <div className="space-y-3">
              {Object.entries(analytics.status).map(([status, count]) => (
                <div key={status}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${getTicketStatusColor(status)}`}>
                      {t(`supportModule.status${status.charAt(0).toUpperCase()}${status.slice(1)}`)}
                    </span>
                    <span className="text-gray-400">{count}</span>
                  </div>
                  <div className="h-2 bg-[#1a1a25] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#006cd4] rounded-full"
                      style={{ width: `${(count / maxStatusCount) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Resolution source */}
        <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
          <h3 className="font-medium mb-4">{t('supportModule.byResolution')}</h3>
          {Object.keys(analytics.resolution).length === 0 ? (
            <p className="text-sm text-gray-500">{t('supportModule.noData')}</p>
          ) : (
            <div className="space-y-3">
              {Object.entries(analytics.resolution).map(([source, count]) => (
                <div key={source}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-300">{resolutionLabels[source] || source}</span>
                    <span className="text-gray-400">{count}</span>
                  </div>
                  <div className="h-2 bg-[#1a1a25] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#00d4aa] rounded-full"
                      style={{ width: `${(count / maxResolutionCount) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Avg resolution + CSAT */}
        <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
          <h3 className="font-medium mb-4">{t('supportModule.avgResolution')}</h3>
          <p className="text-3xl font-bold">
            {analytics.avgResolutionHours}
            <span className="text-lg text-gray-400 ml-1">{t('supportModule.hours')}</span>
          </p>
        </div>

        <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
          <h3 className="font-medium mb-4">{t('supportModule.csat')}</h3>
          {analytics.csat.totalVotes === 0 ? (
            <p className="text-sm text-gray-500">{t('supportModule.noData')}</p>
          ) : (
            <>
              <p className="text-3xl font-bold">
                {analytics.csat.average.toFixed(1)}
                <span className="text-lg text-gray-400"> / 5</span>
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {analytics.csat.totalVotes} {t('supportModule.csatVotes')}
              </p>
            </>
          )}
        </div>
      </div>

      {/* Top articles */}
      <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
        <h3 className="font-medium mb-4">{t('supportModule.topArticles')}</h3>
        {analytics.topArticles.length === 0 ? (
          <p className="text-sm text-gray-500">{t('supportModule.noData')}</p>
        ) : (
          <div className="space-y-2">
            {analytics.topArticles.map((article, index) => (
              <div key={article.id} className="flex items-center justify-between px-3 py-2 bg-[#1a1a25] rounded-lg">
                <span className="text-sm truncate">
                  <span className="text-gray-500 mr-2">{index + 1}.</span>
                  {article.title}
                </span>
                <span className="text-sm text-gray-400 shrink-0 ml-4">
                  {article.view_count} {t('supportModule.views')}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
